import { Box, Typography, Grid } from '@mui/material'
import { EditField } from '~/shared/ui'
import Alert from '@mui/material/Alert'
import { formatDateWithDay } from '~/shared/lib/date'
import type { PatientUser } from '~/shared/types'

interface IProps {
  insuranceInfo: PatientUser['person']['insurance_info']
}

const FEE_FOR_SERVICE = 'FEE_FOR_SERVICE'

const ATTRIBUTION_TYPE_DESCRIPTIONS: Record<string, string> = {
  POC_FORM: 'P.O.C.',
  VISITS: 'Visits',
  PHONECALL: 'Phonecall',
  [FEE_FOR_SERVICE]: 'None (Fee For Service)',
}

const ATTRIBUTION_FAILURE_DESCRIPTIONS: Record<string, string> = {
  UNLICENSED_STATE: 'Patient lives in an unlicensed state.',
  NOT_PPO: 'P.P.O. plan required for this payer and plan.',
  NO_CONTRACT_MATCHES_PAYER_AND_STATE: 'Firefly is not contracted with this payer in this state.',
  INELIGIBLE_PLAN:
    'This insurance plan is marked as not accepted by Firefly. If this is a new plan, it may need to be reviewed.',
  INELIGIBLE_PAYER: 'This insurance payer is marked as not accepted by Firefly.',
  NO_CONTRACT_MATCHING_PAYER: 'Firefly does not have a contract with this payer.',
  NO_OOS_CONTRACT_ALLOWED: 'The contract for this plan does not allow out-of-state patients.',
}

const InsuranceContractInfo = (props: IProps) => {
  const type = props.insuranceInfo?.attribution_requirement_type
  const ineligibleReasonType = props.insuranceInfo?.attribution_failure_reason
  const contractName = props.insuranceInfo?.contract_name

  const typeDescription = (type && ATTRIBUTION_TYPE_DESCRIPTIONS[type]) || type || 'No Result'
  const ineligibleReasonDescription =
    (ineligibleReasonType && ATTRIBUTION_FAILURE_DESCRIPTIONS[ineligibleReasonType]) ||
    ineligibleReasonType
  const attributionState = props.insuranceInfo.insurance_attribution_state

  let timestamp: string | null = null
  if (props.insuranceInfo.insurance_attribution_transition_at) {
    timestamp = formatDateWithDay(new Date(props.insuranceInfo.insurance_attribution_transition_at))
  }

  const isContracted = !!props.insuranceInfo.contract_id

  const eligibilityLabel = (() => {
    if (!isContracted) {
      return 'No Contract found'
    }
    if (props.insuranceInfo?.attribution_requirement_type === FEE_FOR_SERVICE) {
      return 'FFS Eligible'
    }
    return 'PMPM Eligible'
  })()

  return (
    <Box>
      <Typography variant="h6">Contract Info</Typography>
      {!isContracted && ineligibleReasonDescription && (
        <Box sx={{ mt: 1 }}>
          <Alert severity="warning">
            <Box>
              <Typography component="span" variant="inherit">
                <strong>{ineligibleReasonDescription}</strong>
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">({ineligibleReasonType})</Typography>
            </Box>
          </Alert>
        </Box>
      )}
      <Box sx={{ mt: 1 }}>
        <Alert severity={isContracted ? 'success' : 'warning'}>
          <Box sx={{ display: 'block' }}>
            <Typography component="span" variant="inherit">
              <strong>{eligibilityLabel}</strong>
            </Typography>
            {timestamp && (
              <Typography component="span" variant="inherit">
                &nbsp;- Changed {timestamp}
              </Typography>
            )}
          </Box>
        </Alert>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <EditField
              value={contractName ?? ' '}
              label="Contract Name"
              disabled={true}
              margin="none"
            />
            <EditField
              value={typeDescription ?? ' '}
              label="Attribution Requirement"
              disabled={true}
              margin="none"
            />
            <EditField
              value={attributionState ?? ' '}
              label="Attribution State"
              disabled={true}
              margin="none"
            />
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}

export default InsuranceContractInfo
